import mapboxgl from 'mapbox-gl';
import icon from '../../assets/images/icons/mapbox_stream.svg';
import * as uid from 'uuid';
import sector from '@turf/sector';
import midpoint from '@turf/midpoint';
import FOVControlMarker from './FOVControlMarker';
import Utils from '@utils/index';
class StreamMarker extends mapboxgl.Marker {
  private _uid: string = '';
  private _map: mapboxgl.Map | null = null;
  private _label: string = '';
  private _kvsName: string = '';
  private _range: number = 0;
  private _fovWidth: number = 0;
  private _angleEastOfNorth: number = 0;
  private _fovMarker: FOVControlMarker | null = null;
  private _dragUpdate: number = Date.now();
  private _color: string = '#000';
  private _onUpdateFovCallback: (() => void) | null = null;
  private _onFocus: ((marker: StreamMarker) => void) | null = null;

  constructor (options?: mapboxgl.MarkerOptions) {
    super({ ...options, element: document.createElement('div') });
    this.getElement().style.backgroundImage = `url('${icon}')`;
    this.getElement().style.backgroundSize = 'cover';
    this.getElement().style.width = '40px';
    this.getElement().style.height = '40px';
    this.getElement().style.borderRadius = '50%';
    this.getElement().style.cursor = 'pointer';

    this._uid = uid.v4();

    this.getElement().addEventListener('click', () => {
      if (this._onFocus) this._onFocus(this)
    })

    this.on('dragstart', (e: any) => {
      if (this._onFocus) this._onFocus(this)
      if (this._map) {
        this.cleanUp(this._map);
      }
    })

    this.on('dragend', (e: any) => {
      if (this._map) {
        this.paintLabel(this._map, this._label)
        this.paintFov(this._range, this._angleEastOfNorth, this._fovWidth);
      }
    })
  }

  public withMap(map: mapboxgl.Map): StreamMarker {
    this._map = map;
    return this;
  }

  public withKvsName(kvsName: string): StreamMarker {
    this._kvsName = kvsName;
    return this;
  }

  public setInteractable(interactable: boolean): StreamMarker {
    if (interactable) {
      this.getElement().style.opacity = '1';
      this.getElement().style.pointerEvents = 'all';
    } else {
      this.getElement().style.opacity = '0.9';
      this.getElement().style.pointerEvents = 'none';
    }
    return this;
  }

  public setAngleEastOfNorth(value: number): StreamMarker {
    this.setRotation(value - 90);
    return this;
  }

  public withLabel(map: mapboxgl.Map, label: string): StreamMarker {
    this._label = label;
    if (!this.getLngLat()) throw new Error("Labels must be placed after setLngLat");
    this.paintLabel(map, label);
    map.on('style.load', () => {
      this.paintLabel(map, label);
    })

    return this;
  }

  public paintFov(range: number, angleEastOfNorth: number, width: number): void {
    this._range = range;
    this._angleEastOfNorth = angleEastOfNorth;
    this._fovWidth = width;

    if (this._map) {
      const lineSource = this._map.getSource(`fov-lines-canvas-${this._uid}`);

      const geometry = sector(
        [this.getLngLat().lng, this.getLngLat().lat],
        range,
        angleEastOfNorth - (width / 2),
        angleEastOfNorth + (width / 2),
        { units: 'feet', steps: 512 }
      );

      const anchor = sector(
        [this.getLngLat().lng, this.getLngLat().lat],
        range,
        angleEastOfNorth - (1 / 2),
        angleEastOfNorth + (1 / 2),
        { units: 'feet', steps: 1 }
      );

      if (anchor.geometry.coordinates[0][1] && anchor.geometry.coordinates[0][2]) {
        this.setFovMarker(midpoint(anchor.geometry.coordinates[0][1], anchor.geometry.coordinates[0][2]).geometry.coordinates)
      } else {
        this.setFovMarker(geometry.geometry.coordinates[0][Math.floor(geometry.geometry.coordinates[0].length / 2)])
      }

      if (!lineSource) {
        this._map.addSource(`fov-lines-canvas-${this._uid}`, {
          type: 'geojson',
          data: geometry
        })
      }

      const lineSourceCast = lineSource as mapboxgl.GeoJSONSource;
      if (lineSourceCast) {
        lineSourceCast.setData(geometry);
      }

      const lineLayer = this._map.getLayer(`fov-lines-layer-${this._uid}`);
      if (!lineLayer) {
        this._map.addLayer({
          id: `fov-lines-layer-${this._uid}`,
          type: 'line',
          source: `fov-lines-canvas-${this._uid}`,
          layout: {

          },
          paint: {
            "line-color": this._color,
            "line-width": 2,
          }
        })
      }

      const fillLayer = this._map.getLayer(`fov-lines-layer-fill-${this._uid}`);
      if (!fillLayer) {
        this._map.addLayer({
          id: `fov-lines-layer-fill-${this._uid}`,
          type: 'fill',
          source: `fov-lines-canvas-${this._uid}`,
          layout: {

          },
          paint: {
            "fill-color": this._color,
            "fill-opacity": 0.5,
          }
        })
      }

      if (this._onUpdateFovCallback) this._onUpdateFovCallback();
    }
  }

  public updateFovMarker(): void {
    if (this._fovMarker) {
      const geometry = sector(
        [this.getLngLat().lng, this.getLngLat().lat],
        this.getRange(),
        this.getAngleEastOfNorth() - (this.getFovWidth() / 2),
        this.getAngleEastOfNorth() + (this.getFovWidth() / 2),
        { units: 'feet', steps: 512 }
      );

      const anchor = sector(
        [this.getLngLat().lng, this.getLngLat().lat],
        this.getRange(),
        this.getAngleEastOfNorth() - (1 / 2),
        this.getAngleEastOfNorth() + (1 / 2),
        { units: 'feet', steps: 1 }
      );

      if (anchor.geometry.coordinates[0][1] && anchor.geometry.coordinates[0][2]) {
        const position = midpoint(anchor.geometry.coordinates[0][1], anchor.geometry.coordinates[0][2]).geometry.coordinates;
        this._fovMarker.setLngLat(new mapboxgl.LngLat(position[0], position[1]))
      } else {
        const position = geometry.geometry.coordinates[0][Math.floor(geometry.geometry.coordinates[0].length / 2)]
        this._fovMarker.setLngLat(new mapboxgl.LngLat(position[0], position[1]))
      }
    }
  }

  public setFovMarker(position: number[]): void {
    if (this._map) {
      if (!this._fovMarker) {
        const marker = new FOVControlMarker()
          .setLngLat(new mapboxgl.LngLat(position[0], position[1]))
          .setDraggable(true)
          .addTo(this._map);

        this._fovMarker = marker;

        marker.on('dragstart', () => {
          if (this._onFocus) this._onFocus(this)
        })

        marker.on('drag', (e) => {
          if (Date.now() - this._dragUpdate > 20) {
            const event = e as { target: { _lngLat: { lat: number, lng: number } } }

            const lat = event.target._lngLat.lat;
            const lng = event.target._lngLat.lng;

            var angle = Math.atan2(this.getLngLat().lng - lng, this.getLngLat().lat - lat) * 180 / Math.PI + 180;
            this._angleEastOfNorth = +(angle).toFixed(2);

            const distance = Utils.MapBox.getDistance(this.getLngLat().lat, this.getLngLat().lng, lat, lng, "K");
            this._range = +(distance * 3280.84).toFixed(2)

            this._dragUpdate = Date.now();
            this.paintFov(this._range, this._angleEastOfNorth, this._fovWidth);
          }
        })
      }
    }
  }

  public withFov(range: number, angleEastOfNorth: number, fovWidth: number): StreamMarker {
    this._range = range;
    this._angleEastOfNorth = angleEastOfNorth;
    this._fovWidth = fovWidth;
    this.paintFov(range, angleEastOfNorth, fovWidth)
    return this;
  }

  public removeFov() {
    const mapbox = this._map;
    if (mapbox) {
      try { mapbox.removeLayer(`fov-lines-layer-${this._uid}`); } catch (e) { }
      try { mapbox.removeLayer(`fov-lines-layer-fill-${this._uid}`); } catch (e) { }
      try { mapbox.removeSource(`fov-lines-canvas-${this._uid}`); } catch (e) { }
      try { this._fovMarker?.remove(); this._fovMarker = null; } catch (e) { }
    }
  }

  public cleanUp(map?: mapboxgl.Map): void {
    const mapbox = map || this._map;
    if (mapbox) {
      try { mapbox.removeLayer(`label-layer-${this._uid}`); } catch (e) { }
      try { mapbox.removeSource(`label-source-${this._uid}`); } catch (e) { }
      try { mapbox.removeLayer(`fov-lines-layer-${this._uid}`); } catch (e) { }
      try { mapbox.removeLayer(`fov-lines-layer-fill-${this._uid}`); } catch (e) { }
      try { mapbox.removeSource(`fov-lines-canvas-${this._uid}`); } catch (e) { }
      try { this._fovMarker?.remove(); this._fovMarker = null; } catch (e) { }
    }
  }

  public repaintLabel() {
    if (this._map && this._label)
      this.paintLabel(this._map, this._label);
  }

  private paintLabel(map: mapboxgl.Map, label: string): void {
    const feature: GeoJSON.Feature = {
      type: 'Feature',
      properties: {
        description: label
      },
      geometry: {
        type: 'Point',
        coordinates: [this.getLngLat().lng, this.getLngLat().lat]
      }
    }

    const styleName = map.getStyle().name?.toLowerCase();

    let fontColor = 'black';
    let haloColor = 'white';

    if (styleName?.includes('satellite') || styleName?.includes('dark')) {
      fontColor = 'white'
      haloColor = 'black';
    };

    map.addSource(`label-source-${this._uid}`, {
      type: 'geojson',
      data: feature
    })

    map.addLayer({
      id: `label-layer-${this._uid}`,
      type: 'symbol',
      source: `label-source-${this._uid}`,
      layout: {
        'text-field': ['get', 'description'],
        'text-variable-anchor': ['bottom'],
        'text-radial-offset': 1,
        'text-justify': 'auto',
        'text-size': 18,
        'text-font': ['Open Sans Extrabold']
      },
      paint: {
        'text-color': fontColor,
        'text-opacity': 1,
        'text-halo-color': haloColor,
        'text-halo-width': 2
      }
    })
  }

  public getKvsName(): string {
    return this._kvsName;
  }

  public getAngleEastOfNorth() {
    return this._angleEastOfNorth
  }

  public getFovWidth() {
    return this._fovWidth;
  }

  public getRange() {
    return this._range;
  }

  public onUpdateFov(callback: () => void): void {
    this._onUpdateFovCallback = callback;
  }

  public onFocus(callback: (marker: StreamMarker) => void): void {
    this._onFocus = callback;
  }

  public setColor(color: string): void {
    this._color = color;
    try {
      if (this._map) {
        const lineLayer = this._map.getLayer(`fov-lines-layer-${this._uid}`);
        if (lineLayer) {
          this._map.removeLayer(lineLayer.id)
          this._map.addLayer({
            id: `fov-lines-layer-${this._uid}`,
            type: 'line',
            source: `fov-lines-canvas-${this._uid}`,
            layout: {

            },
            paint: {
              "line-color": this._color,
              "line-width": 2,
            }
          })
        }

        const fillLayer = this._map.getLayer(`fov-lines-layer-fill-${this._uid}`);
        if (fillLayer) {
          this._map.removeLayer(fillLayer.id)
          this._map.addLayer({
            id: `fov-lines-layer-fill-${this._uid}`,
            type: 'fill',
            source: `fov-lines-canvas-${this._uid}`,
            layout: {

            },
            paint: {
              "fill-color": this._color,
              "fill-opacity": 0.5,
            }
          })
        }
      }
    } catch (error) {

    }
  }
}


export default StreamMarker;
